import { useEffect, useMemo, useState } from 'react'
import PublicLayout from 'layouts/PublicLayout'
import placeholderImage from 'assets/images/placeholder.png'
import ApiClient from 'data/api/api_client'
import { useQuery } from 'react-query'
import { useRouter } from 'next/router'
import AvailabilityForm from 'components/property/AvailabilityForm'
import {
  CheckCircleIcon,
  LoadingIcon,
  StarIcon,
  StarLeftHalfIcon,
  StarRightHalfIcon,
} from 'components/icons'
import Map from 'components/Map'
import dayjs from 'dayjs'
import BecomeAMemberSection from 'components/sections/BecomeAMemberSection'
import queryCache from 'data/queryCache'
import { NextSeoProps } from 'next-seo'
import SimilarPropertiesSection from 'components/sections/SimilarPropertiesSection'
import CarouselBedroomsBathrooms from 'components/carousels/CarouselBedroomsBathrooms'
import PropertyAmenitiesSection from 'components/property/PropertyAmenitiesSection'
import CarouselImages from 'components/carousels/CarouselImages'
import { setArrival, setDeparture, setAdults, setChildren } from 'store/booking-request'
import { useDispatch } from 'react-redux'
import Button from 'components/Button'
import Image from 'components/Image'
import { formatDateReadable } from 'data/helpers/dates'
import { config } from 'data/config'
import { graphql } from 'gql'
import { useQuery as useGraphQlQuery } from 'urql'
import BookingConditionsSection from 'components/property/booking-conditions/Section'
import ServiceCarouselSection from 'components/sections/services/ServiceCarouselSection'
import RestReview from 'data/types/review'
import MagazineCarouselSection from 'components/sections/magazine/MagazineCarouselSection'
import { ReviewPropertySlugResponse } from 'data/api/reviews_api_client'
import PageHeader from 'components/sections/page/PageHeader'
import { joinBy, slugify } from '@ama-selections/ui'
import PropertyDescriptionSection from 'components/property/PropertyDescriptionSection'
import Headers from 'components/home/common/typography/Headers'
import Paragraphs from 'components/home/common/typography/Paragraphs'
import { PropertyPageSections } from 'components/property/PropertyNavbar'
import { MagazineArticlesPadResults } from 'gql/graphql'
import PropertyNavbar from 'components/property/PropertyNavbar'
import WishlistModalTrigger from 'components/modals/triggers/WishlistModalTrigger'
import PropertyStickyFooter from 'components/property/PropertyStickyFooter'
import SectionHeaders from 'components/home/common/typography/SectionHeaders'
import { useHasScrolled } from 'data/hooks/useHasScrolled'
import { MobileScreenWidths, useScreenWidth } from 'data/hooks/useScreenWidth'
import { useMobileApp } from 'data/hooks/MobileApp/useMobileApp'

const PROPERTY_QUERY = graphql(`
  query PropertyPage($slug: String!) {
    property(slug: $slug) {
      id
      title
      reference
      slug
      url
      currency
      main_image {
        id
        url
        file_name
      }
      main_image_alt_text
      images {
        id
        url
        file_name
      }
      sub_region {
        id
        value
      }
      region {
        id
        value
        slug
      }
      country {
        id
        value
      }
      seo {
        title
        description
      }
      price_range {
        min
        max
      }
      sub_heading
      bedrooms
      bathrooms
      guests
      house_sq
      description
      public_events {
        id
        start_date
        end_date
      }
      # Bedrooms
      bedroom_arrangements {
        id
        bedroom_type {
          id
          value
          media {
            id
            url
          }
        }
        bedroom_outdoor_space {
          id
          value
        }
        bedroom_ac_type {
          id
          value
        }
        bedroom_toilet_access {
          id
          value
        }
        bedroom_wash_facility {
          id
          value
        }
      }
      # Amenities
      general_amenities {
        id
        value
        parent {
          id
        }
      }
      entertainment_amenities {
        id
        value
        parent {
          id
        }
      }
      outdoor_amenities {
        id
        value
        parent {
          id
        }
      }
      # Services
      housekeeping_services {
        id
        value
      }
      linen_towels_services {
        id
        value
      }
      staff_services {
        id
        value
      }
      other_services {
        id
        value
      }
      # Nearest distances
      nearest_distance_one {
        id
        name
        value
        distance
      }
      nearest_distance_two {
        id
        name
        value
        distance
      }
      nearest_distance_three {
        id
        name
        value
        distance
      }
      nearest_distance_four {
        id
        name
        value
        distance
      }
      position {
        latitude
        longitude
      }
      # Booking conditions
      house_rules {
        id
        value
      }
      additional_costs {
        id
        value
      }
      property_rental_due {
        id
        value
      }
      security_deposit {
        id
        value
      }
      additional_house_rules
      extra_additional_costs
    }
  }
`)

const PropertyPage = () => {
  const router = useRouter()
  const dispatch = useDispatch()
  const hasScrolled = useHasScrolled(0, true)
  const { isMobile } = useScreenWidth(MobileScreenWidths.LG)
  const { isMobileApp } = useMobileApp()

  const { arrival, departure, adults, children } = router.query
  useEffect(() => {
    dispatch(setArrival(arrival ? formatDateReadable(arrival as string) : null))
    dispatch(setDeparture(departure ? formatDateReadable(departure as string) : null))
    dispatch(setAdults(adults ? parseInt(adults as string) : 0))
    dispatch(setChildren(children ? parseInt(children as string) : 0))
  }, [arrival, departure, adults, children])

  const [{ data }] = useGraphQlQuery({
    query: PROPERTY_QUERY,
    variables: {
      slug: router.query.property as string,
    },
    pause: !router.query.property,
  })

  const property = useMemo(() => data?.property, [data])

  useQuery(
    ['calendar'],
    () => ApiClient.calendar.getEventsPublic(property!.id),
    {
      enabled: !!property?.id,
    },
  )

  const [reviews, setReviews] = useState<ReviewPropertySlugResponse>({
    reviews: [],
    totalReviews: 1,
    average: 0,
    page: 1,
  })

  const perPage = 3
  const [page, setPage] = useState(1)

  const { isFetching: isFetchingReviews, refetch: refetchReviews } = useQuery(
    queryCache.reviewsForProperty(router.query.property),
    () => ApiClient.reviews.getReviews({
      property_slug: property?.slug,
    }, {
      page: page,
      per_page: perPage,
    }),
    {
      enabled: reviews?.reviews?.length < reviews?.totalReviews || !!property?.slug,
      onSuccess: (data) => {
        const response: ReviewPropertySlugResponse = data as ReviewPropertySlugResponse

        if (response) {
          const oldReviews = reviews.reviews ?? []
          const newReviews = response.reviews ?? []

          setReviews({
            average: response.average,
            reviews: [...oldReviews, ...newReviews],
            totalReviews: response.totalReviews,
            page: response.page,
          })
        }
      },
    },
  )

  useEffect(() => {
    if (page != Number(reviews.page)) {
      refetchReviews()
    }
  }, [page])

  const images = useMemo(() => {
    if (!data?.property) { return [] }

    let newImages: ({ url?: string, alt_text?: string })[] = []

    newImages = (data.property.images ?? []).map((image) => ({
      url: image.url ?? undefined,
      alt_text: image.file_name,
    }))

    if (data.property.main_image) {
      newImages.unshift({
        url: data.property.main_image.url ?? undefined,
        alt_text: data.property.main_image_alt_text ?? data.property.main_image.file_name ?? data.property.title,
      })
    }

    return newImages.filter((image) => image.url !== undefined) as ({ url: string, alt_text: string })[]
  }, [data?.property])

  useEffect(() => {
    if (router?.query?.arrival && router?.query?.departure) {
      dispatch(setArrival(router?.query?.arrival))
      dispatch(setDeparture(router?.query?.departure))
    }
  }, [dispatch, router?.query?.arrival, router?.query?.departure])

  const nearest = useMemo(() => ([
    data?.property?.nearest_distance_one,
    data?.property?.nearest_distance_two,
    data?.property?.nearest_distance_three,
    data?.property?.nearest_distance_four,
  ]?.filter(item => item !== null)), [
    data?.property?.nearest_distance_one,
    data?.property?.nearest_distance_two,
    data?.property?.nearest_distance_three,
    data?.property?.nearest_distance_four,
  ])

  const [isFullScreenImageModalOpen, setIsFullscreenImageModalOpen] = useState<boolean>(false)

  if (!property) { return null }

  const seo: NextSeoProps = {
    title: property.seo.title ?? property.title,
    description: property.seo.description ?? undefined,
    openGraph: {
      type: 'website',
      url: `${config.appURL}/${property.url}`,
      images: property.main_image?.url
        ? [
          {
            url: property.main_image.url,
            width: 922,
            height: 700,
          },
        ]
        : [],
    },
  }

  return (
    <PublicLayout
      seo={seo}
      mobileNavBar={{
        images: images,
        wishlist: {
          id: property.id,
          model: 'Property',
        },
        profileTrigger: false,
        visible: !hasScrolled,
        back: true,
        fade: true,
      }}
      actionFooter={{
        element: <PropertyStickyFooter property={property} />,
      }}
      temporaryNavbar={<PropertyNavbar property={property} />}
    >
      <div className="flex flex-col w-full antialiased lg:flex-col-reverse">
        <div className="hidden lg:block">
          {
            (property.images ?? []).length > 0 && (
              <CarouselImages
                images={images}
                hasLoadedProps={{ rootMargin: '500px' }}
                isFullScreenImageModalOpen={isFullScreenImageModalOpen}
                onFullScreenImageModalChange={(isOpen: boolean) => setIsFullscreenImageModalOpen(isOpen)}
              />
            )
          }
        </div>
        <div>
          <hr className="hidden text-grey-200 lg:block" />
          <div className="flex flex-col lg:flex-row lg:w-full justify-center max-w-[1800px] mx-auto">
            <div className="hidden lg:block xl:max-w-[992px] w-full lg:h-[700px] relative" >
              <Image
                src={property.main_image?.url ?? placeholderImage}
                alt={property.main_image_alt_text ?? property.title}
                className="absolute inset-0 object-cover"
                sizes="50vw"
                layout="fill"
                priority={true}
                onClick={() => setIsFullscreenImageModalOpen(true)}
              />

              <WishlistModalTrigger
                propertyId={property.id}
                className="absolute z-10 top-10 right-10"
                buttonVariant="bigProperty"
              />
            </div>

            <div
              id={PropertyPageSections.GENERAL}
              className="flex flex-col items-center justify-center w-full xl:max-w-[40%] 3xl:max-w-[808px] scroll-mt-safe-offset-100"
            >
              <PageHeader
                label={property.reference}
                heading={property.title}
                subheading={joinBy([property.sub_region.value, property.region.value])}
              />

              <div className="hidden w-full px-20 lg:block">
                <AvailabilityForm property={property} className="py-40 mt-35 lg:py-0" />
              </div>
            </div>
          </div>

          <hr className="hidden text-grey-200 lg:block" />

          <div className="flex flex-col items-center container-public my-35 lg:my-75">
            <div className="max-w-[900px] w-full">
              {
                property.sub_heading && (
                  <Headers.XXL className="mb-40 text-center lg:!font-normal">
                    {property.sub_heading}
                  </Headers.XXL>
                )
              }

              {
                property.reference && (
                  <SectionHeaders.LG className="hidden mb-40 text-center lg:block -mt-25 text-primary-fresh">
                    Ref. {property.reference}
                  </SectionHeaders.LG>
                )
              }

              <PropertyDescriptionSection property={property} />

              {property.description && (
                <div
                  className="text-justify mt-[56px] text-grey-750 leading-23 notification-content text-14 lg:text-15"
                  dangerouslySetInnerHTML={{ __html: property.description }}
                />
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Bedrooms */}
      <div
        id={PropertyPageSections.BEDS_BATHS}
        className="max-w-[1450px] mx-auto lg:px-20 mt-50 lg:mt-100 flex flex-col gap-35 lg:gap-50 scroll-mt-safe-offset-100"
      >
        <Headers.XXL className="text-center px-70 text-grey-900">
          Bedrooms &amp; Bathrooms
        </Headers.XXL>

        <CarouselBedroomsBathrooms bedrooms={property.bedroom_arrangements ?? []} />
      </div>

      {/* Amenities */}
      <div
        id={PropertyPageSections.AMENITIES}
        className="flex flex-col items-center container-public mt-50 lg:mt-100 scroll-mt-safe-offset-100"
      >
        <PropertyAmenitiesSection property={property} />
      </div>

      {/* Nearest */}
      <div
        id={PropertyPageSections.LOCATION}
        className="flex flex-col items-center my-50 lg:mt-100 container-public scroll-mt-safe-offset-100"
      >
        <Headers.XXL className="text-center px-70 text-grey-900 mt-50 scroll-mt-safe-offset-100">
          Location
        </Headers.XXL>

        <div className="flex flex-wrap justify-center w-full gap-40 mt-45 lg:mt-60 lg:gap-x-0">
          {(nearest ?? []).map((item, index) => (
            <div className="flex flex-col items-center max-w-[300px] w-full" key={`${index}-${item?.value}`}>
              <Paragraphs.PropertyPage className="w-full antialiased font-bold text-center uppercase text-grey-800 tracking-4">
                Nearest {item?.name}
              </Paragraphs.PropertyPage>

              <Paragraphs.PropertyPage className="w-full antialiased text-center mt-15 text-grey-500">
                {item?.value}, {item?.distance} min.
              </Paragraphs.PropertyPage>
            </div>
          ))}
        </div>
      </div>

      <Map
        id={PropertyPageSections.MAP}
        center={{
          lat: property.position.latitude,
          lng: property.position.longitude,
        }}
        circleRadius={400}
        height={
          isMobileApp || isMobile
            ? '!h-[356px]'
            : undefined
        }
      />

      <MagazineCarouselSection
        id={PropertyPageSections.MAGAZINE}
        className={{
          container: 'mt-45 mb-60 lg:my-150',
        }}
        subheading="GET TO KNOW THE AREA"
        heading={'Start planning your stay\n in this beautiful\n destination'}
        description="Get inspired by our carefully curated travel guides and top picks of things to do and places to see. From the best restaurants to immersive activities and festivals, our recommendations are designed with you in mind."
        button={{
          text: 'Read More',
          href: '/magazine',
        }}
        filters={{
          country: slugify(property.country.value),
          region: slugify(property.region.value),
          subregion: slugify(property.sub_region.value),
          padResults: MagazineArticlesPadResults.True,
        }}
      />

      <hr className="text-grey-200" />

      <ServiceCarouselSection
        id={PropertyPageSections.SERVICES}
        className={{
          container: 'mt-45 mb-60 lg:my-150',
        }}
        subheading="SERVICES &amp; EXPERIENCES"
        heading={'Seamlessly add bespoke\n services & experiences\n to your stay'}
        description="Our in-house concierge team will assist you in tailoring your stay to create an unforgettable holiday. Request services, recommendations and share your trip itinerary with friends and family directly from your account."
        button={{
          text: 'View Services',
          href: '/services',
        }}
        filters={{
          region: property.region.id,
          is_featured: true,
        }}
      />

      {
        reviews.reviews?.length
          ? <>
            <hr className="text-grey-200" />

            <div className="flex items-center justify-center container-public">
              <div className="w-full max-w-4xl py-100">
                <h3 className="font-serif text-center text-36">Guest Reviews</h3>
                <div className="flex justify-center">

                  <div className="relative flex justify-center mt-50 lg:mt-100 text-24 w-min">
                    {
                      [...Array(6).keys()].map((item, index) => {
                        const difference = reviews.average - index

                        return index !== 0
                          && (index <= reviews.average
                            ? <StarIcon className="text-primary" key={index} />
                            : (difference < 0 && difference >= -0.5)
                              ? <div className="flex" key={index}>
                                <StarLeftHalfIcon className="text-primary" />
                                <StarRightHalfIcon className="text-grey-250" />
                              </div>
                              : <StarIcon className="text-grey-250" key={index} />
                          )
                      })
                    }
                  </div>
                </div>

                <p className="mt-10 text-center text-grey-750 tracking-1 leading-23">
                  Rated {reviews.average} out of 5 based on {reviews.totalReviews} reviews
                </p>

                <div className="flex items-center justify-center w-full">
                  <div className="w-full max-w-4xl">
                    <div className="flex flex-col gap-40 mt-40">
                      {
                        reviews.reviews.map((review: RestReview) => (
                          <div className="flex flex-col lg:gap-20 gap-15 lg:flex-row lg:justify-between" key={`property-review-${review.id}`}>
                            <div>
                              <p className="text-18 text-grey-800 leading-23 tracking-1">{review.name}</p>
                              <p className="text-grey-500 leading-23">{dayjs(review.reviewed_at).format('MMMM DD, YYYY')}</p>
                            </div>
                            <div className="pl-24 lg:pl-0 w-full max-w-[683px]">
                              <div className="flex antialiased text-24 text-primary">
                                {Array(review.rating).fill(null).map(index => (
                                  <StarIcon key={index} />
                                ))}
                              </div>
                              <div className="flex items-center gap-5 mt-5 text-14 text-grey-750">
                                <CheckCircleIcon />
                                <p>collected via trustpilot</p>
                              </div>
                              <p className="text-grey-750 mt-15 leading-23">{review.text}</p>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center gap-30 mt-30">
                  {isFetchingReviews
                    && <LoadingIcon />
                  }
                  {(page * perPage < reviews.totalReviews)
                    && <Button
                      className="max-w-[200px] w-full"
                      onClick={() => setPage(page * perPage < reviews.totalReviews ? page + 1 : page)}
                    >
                      View more
                    </Button>
                  }
                </div>
              </div>
            </div>
          </>
          : null
      }

      <div className="justify-center w-full border-t border-b border-grey-200">
        <div className="container-public my-50 lg:my-100">
          <Headers.XXL
            id={PropertyPageSections.BOOKING_CONDITIONS}
            className="mt-40 text-center mb-30 lg:mb-60 px-30 text-grey-900 scroll-mt-safe-offset-100"
          >
            Booking Conditions
          </Headers.XXL>

          <BookingConditionsSection property={property} />
        </div>
      </div>

      <SimilarPropertiesSection propertySlugOrId={property?.id} />
      <BecomeAMemberSection />
    </PublicLayout>
  )
}

export default PropertyPage

// export async function getServerSideProps ({ query }: NextPageContext) {
//   const response = await urqlClient
//     ?.query(PROPERTY_QUERY, { slug: query.property as string })
//     .toPromise()

//   if (!response?.data?.property) {
//     return { notFound: true }
//   }

//   return {
//     props: {
//       urqlState: ssrCache.extractData(),
//     },
//   }
// }
